// ─────────────────────────────────────────────────────────────────────────────
// algolia-layout-template.js — CB Shopify theme
//
// Hooks for the full collection/search page layout:
//   beforeInstantSearchMainTemplate — filter drawer + grid scaffold
//   beforeInstantSearchStatsTemplate — "N products" count
//   beforeInstantSearchFacetParamsOptions — size variant-group scoping
//
// Depends on window.CBAlgolia (algolia-shared.js must load first).
// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener('algolia.hooks.initialize', function () {
  const { settings, icons } = window.CBAlgolia

  // ── US-3: InstantSearch full page layout (drawer filter — mirrors native drawer mode) ──
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchMainTemplate',
    (_defaultTemplate, data, html) => {
      const caretIcon = icons.caret(html)
      const arrowIcon = icons.arrow(html)
      const closeIcon = icons.close(html)

      return html`
        <div>
          <!-- Required mount points — hidden; handled by native UI or Autocomplete -->
          <div class="ais-input" hidden>
            <div class="ais-search-box-container"></div>
            <div class="ais-input-button">
              <div class="ais-clear-input-icon"></div>
            </div>
          </div>
          <div class="ais-facets-button" hidden></div>
          <!-- ais-change-display must be a descendant of ais-search-header -->
          <div class="ais-search-header" hidden>
            <div class="ais-change-display">
              <span
                class="ais-change-display-block ais-change-display-selected"
              ></span>
              <span class="ais-change-display-list"></span>
            </div>
          </div>

          <!-- Facets toolbar — hidden until Algolia results arrive (avoids empty sort/stats flash) -->
          <div
            class="facets-container facets-container-drawer mb-30 container"
            style="display:none"
            data-algolia-facets-toolbar
          >
            <!-- Filter drawer trigger + panel — exact mirror of facets.liquid drawer structure -->
            <menu-drawer
              class="mobile-facets__wrapper flex-grow-1"
              data-breakpoint="mobile"
              data-facet-drawer
            >
              <details class="mobile-facets__disclosure disclosure-has-popup">
                <summary class="mobile-facets__open-wrapper focus-offset">
                  <span class="mobile-facets__open">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                      focusable="false"
                      role="presentation"
                      class="icon icon-filter"
                      fill="none"
                      viewBox="0 11 20 20"
                    >
                      <line
                        x1="16.5"
                        y1="17.5"
                        x2="3.5"
                        y2="17.5"
                        stroke="currentColor"
                        stroke-linecap="round"
                      />
                      <line
                        x1="16.5"
                        y1="24.5"
                        x2="3.5"
                        y2="24.5"
                        stroke="currentColor"
                        stroke-linecap="round"
                      />
                      <circle
                        cx="13"
                        cy="24.5"
                        r="2"
                        fill="white"
                        stroke="currentColor"
                      />
                      <circle
                        cx="7"
                        cy="17.5"
                        r="2"
                        fill="white"
                        stroke="currentColor"
                      />
                    </svg>
                    <span
                      class="mobile-facets__open-label button-label medium-hide large-up-hide"
                    >
                      ${algoliaShopify.translations.filterBy || 'Filter'}
                    </span>
                    <span
                      class="mobile-facets__open-label button-label small-hide"
                    >
                      ${algoliaShopify.translations.filterBy || 'Filter'}
                    </span>
                  </span>
                  <span
                    tabindex="0"
                    class="mobile-facets__close mobile-facets__close--no-js"
                  >
                    ${closeIcon}
                  </span>
                </summary>

                <!-- Intermediate non-positioned wrapper mirrors <facet-filters-form> in facets.liquid.
                     Prevents position:fixed on .mobile-facets from escaping <details> hiding. -->
                <div>
                  <div class="mobile-facets">
                    <div class="mobile-facets__inner gradient">
                      <div class="mobile-facets__header">
                        <div class="mobile-facets__header-inner">
                          <h2
                            class="mobile-facets__heading medium-hide large-up-hide"
                          >
                            ${settings.filterAndSortLabel || 'Filter and sort'}
                          </h2>
                          <h2 class="mobile-facets__heading small-hide">
                            ${algoliaShopify.translations.filterBy || 'Filter'}
                          </h2>
                          <p class="mobile-facets__count"></p>
                        </div>
                      </div>

                      <div class="mobile-facets__main has-submenu gradient">
                        ${data.facets.map(
                          (facet, index) => html`
                            <details
                              class="mobile-facets__details js-filter"
                              data-index="${index + 1}"
                            >
                              <summary
                                class="mobile-facets__summary focus-inset"
                              >
                                <div>
                                  <span>${facet.title}</span>
                                  <span class="mobile-facets__arrow"
                                    >${arrowIcon}</span
                                  >
                                </div>
                              </summary>
                              <div class="mobile-facets__submenu gradient">
                                <button
                                  class="mobile-facets__close-button link link--text focus-inset"
                                  aria-expanded="true"
                                  type="button"
                                >
                                  ${arrowIcon}
                                  <span>${facet.title}</span>
                                </button>
                                <div class="ais-facet-widget-container">
                                  ${(() => {
                                    const n = (facet.name || '').toLowerCase()
                                    const t = (facet.title || '').toLowerCase()
                                    const isProductType =
                                      n === 'product_type' ||
                                      t.includes('product type') ||
                                      t === 'type'
                                    return isProductType
                                      ? html`
                                          <product-type-facet-panel>
                                            <div
                                              class="ais-facet-${facet.escapedName}-container"
                                            ></div>
                                          </product-type-facet-panel>
                                        `
                                      : html`<div
                                          class="ais-facet-${facet.escapedName}-container"
                                        ></div>`
                                  })()}
                                </div>
                                <div class="mobile-facets__footer gradient">
                                  <button
                                    type="button"
                                    class="button button--primary"
                                    onClick="${(e) =>
                                      e.currentTarget
                                        .closest('[data-facet-drawer]')
                                        .querySelector('summary')
                                        .click()}"
                                  >
                                    ${algoliaShopify.translations.apply ||
                                    'Apply'}
                                  </button>
                                </div>
                              </div>
                            </details>
                          `
                        )}
                        ${data.multipleSortOrders
                          ? html`
                              <div
                                class="mobile-facets__details js-filter medium-hide large-up-hide"
                              >
                                <div class="mobile-facets__summary">
                                  <div class="mobile-facets__sort">
                                    <label for="ais-sort-mobile"
                                      >${settings.sortByLabel ||
                                      'Sort by'}</label
                                    >
                                    <div class="select">
                                      <select
                                        class="select__select"
                                        id="ais-sort-mobile"
                                        data-algolia-mobile-sort
                                      ></select>
                                      ${caretIcon}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            `
                          : ''}

                        <div class="mobile-facets__footer">
                          <div class="ais-clear-refinements-container"></div>
                          <button
                            type="button"
                            class="no-js-hidden button button--primary"
                            onClick="${(e) =>
                              e.currentTarget
                                .closest('[data-facet-drawer]')
                                .querySelector('summary')
                                .click()}"
                          >
                            ${algoliaShopify.translations.apply || 'Apply'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            </menu-drawer>

            <!-- Sort dropdown (desktop) -->
            ${data.multipleSortOrders &&
            html`
              <div class="facets small-hide">
                <div class="facet-filters sorting caption">
                  <div class="facet-filters__field">
                    <h2 class="facet-filters__label caption-large text-body">
                      <label>${settings.sortByLabel}</label>
                    </h2>
                    <div class="select">
                      <div class="ais-sort-orders-container"></div>
                      ${caretIcon}
                    </div>
                  </div>
                </div>
              </div>
            `}

            <!-- Result count — must precede active-facets so it sits on the same flex row
                 as the filter button and sort dropdown. active-facets is full-width and
                 wraps to its own row below. -->
            <div class="product-count light" role="status">
              <div class="ais-stats-container"></div>
            </div>

            <!-- Active filter pills — visible on all breakpoints (mirrors native horizontal mode).
                 Row is hidden via JS (display:none) when no filters are active, so it never
                 takes up space. .active-facets-desktop keeps the existing desktop CSS scoping. -->
            <div
              class="active-facets active-facets-desktop"
              style="display:none"
            >
              <div class="ais-current-refined-values-container"></div>
              <div
                class="ais-clear-refinements-container"
                style="display:none"
              ></div>
            </div>
          </div>

          <!-- Full-width product grid + pagination -->
          <div class="container">
            <div class="product-grid-container">
              <div class="ais-hits-container ais-results-as-block"></div>
              <div class="ais-pagination-container"></div>
            </div>
          </div>
        </div>
      `
    }
  )

  // Override the Algolia Stats widget template so nbHits renders as "N products"
  // instead of the default "N results found in Xs". Also syncs the count shown
  // inside the filter drawer header (.mobile-facets__count).
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchStatsTemplate',
    (_defaultTemplate, data, html) => {
      const n = data.nbHits
      const word =
        n === 1
          ? settings.productLabelSingular || 'product'
          : settings.productLabelPlural || 'products'
      const formatted = `${algoliaShopify.helpers.formatNumber(n)} ${word}`
      const drawerCount = document.querySelector('.mobile-facets__count')
      if (drawerCount) drawerCount.textContent = formatted
      return html`<span class="ais-Stats-text">${formatted}</span>`
    }
  )

  // ── Size facet variant-group filtering ──────────────────────────────────────
  // Only exposes size values that belong to the current collection's variant_group.
  // Falls back to all values when:
  //   • search page / no metaobject match → variantGroupOptions is []
  //   • the facet title does not contain "size"
  // Uses transformItems (data-layer) — no DOM observation needed.
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchFacetParamsOptions',
    (params, facet) => {
      const variantGroupOptions =
        window.CBAlgolia.collectionData.variantGroupOptions || []

      if (
        !(facet.title || '').toLowerCase().includes('size') ||
        variantGroupOptions.length === 0
      ) {
        return params
      }

      const normOptions = new Set(
        variantGroupOptions.map((o) => o.trim().toLowerCase())
      )

      return {
        ...params,
        transformItems(items) {
          return items.filter((item) =>
            normOptions.has((item.value || '').trim().toLowerCase())
          )
        },
      }
    }
  )
})
